import { config } from '@/config';
import {
  capitalizeFirstLetter,
  getDetailsBreadcrumbs,
  hasFeatureSetting,
  isSistaminuten as isSistaminutenInstance,
  limitString,
  url,
} from '@/helpers';
import { getBreadcrumbsSchema, getLocalBusinessSchema } from '@/helpers/jsonld';
import { getMerchantKlarnaClientId } from '@/helpers/placeHelperTS';
import { Place } from '@/types/state/shared';
import { Helmet } from 'react-helmet';

const Seo = ({ place, isSistaminuten }: { place: Place; isSistaminuten?: boolean }) => {
  const isExternalPlace = place.isExternalPlace;
  const placeCity = capitalizeFirstLetter(place.contact?.address?.city);
  const serviceNames = place.services?.length
    ? place.services.flatMap((category) => category.services?.map((s) => s.name) || [])
    : [];
  const uniqueServiceNames = serviceNames.filter((item, pos) => Boolean(item) && serviceNames.indexOf(item) === pos);
  const title = limitString(
    `${place.about.name}${place.contact?.address?.city ? ', ' + placeCity : ''}${
      place.contact?.address?.district ? ', ' + place.contact.address.district : ''
    } - Bokadirekt`,
    60,
  );
  const description = limitString(place.about.description, 155);
  const keywords = `${place.about.name}, ${placeCity}, ${uniqueServiceNames.join(', ')}`;
  const placeUrl = `${url.getBaseUrl()}${isExternalPlace ? 'profile-' : ''}places/${place.about.slug}-${place.id}`;
  const image = place.about?.profileImage ? <meta property="og:image" content={place.about.profileImage} /> : null;
  const images = place.about?.images?.length
    ? place.about.images.map((imageUrl, i) => <meta key={i} property="og:image" content={imageUrl} />)
    : null;
  const streetAddress = `${place.contact.address.street || ''}, ${placeCity}`;
  const isNoIndex =
    hasFeatureSetting(place, 'seo_noindex') ||
    hasFeatureSetting(place, 'seo_noindex_portal') ||
    isSistaminutenInstance();

  const klarnaMerchantClientId = getMerchantKlarnaClientId(place);
  const breadcrumbs = getDetailsBreadcrumbs(place).map((item) => ({
    ...item,
    url: `${url.getBaseUrl()}${(item.url ?? '').slice(1)}`,
  }));

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={placeUrl} />
      <meta property="og:url" content={placeUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="Bokadirekt" />
      <meta property="og:locale" content="sv_SE" />
      {image}
      {images}
      <meta name="og:type" content="business.business" />
      <meta name="business:contact_data:street_address" content={streetAddress} />
      <meta name="business:contact_data:postal_code" content={place.contact.address.zipcode || ''} />
      <meta name="business:contact_data:locality" content={placeCity} />
      <meta name="business:contact_data:country_name" content="Sverige" />
      <meta
        name="place:location:latitude"
        content={place.contact.position.lat ? place.contact.position.lat.toString() : ''}
      />
      <meta
        name="place:location:longitude"
        content={place.contact.position.lon ? place.contact.position.lon.toString() : ''}
      />
      {place.contact.phone && <meta name="business:contact_data:phone_number" content={place.contact.phone || ''} />}
      {!isSistaminuten && <meta name="twitter:card" content="summary_large_image" />}
      {!isSistaminuten && <meta name="twitter:site" content="@bokadirekt" />}
      <meta name="robots" content={isNoIndex ? 'noindex, follow' : 'index, follow'} />
      {klarnaMerchantClientId && (
        <script
          async
          data-environment={config.env === 'production' ? 'production' : 'playground'}
          src="https://js.klarna.com/web-sdk/v1/klarna.js"
          data-client-id={klarnaMerchantClientId}></script>
      )}
      <script type="application/ld+json">{getLocalBusinessSchema(place)}</script>
      <script type="application/ld+json">{getBreadcrumbsSchema(breadcrumbs)}</script>
    </Helmet>
  );
};

export default Seo;
